import { Component, OnInit, effect, signal, inject, input } from '@angular/core';
import moment from 'moment';
import { combineLatest, map, take } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { Dashboard } from 'src/app/core/models/dashboard';
import { GoalsPlanning } from 'src/app/core/models/goals-planning';
import { Mongo } from 'src/app/core/models/mongo';
import { Tenant } from 'src/app/core/models/tenant';
import { adjustHexColor } from 'src/app/shared/utils/shared';
import { StatsService } from '../../services/stats.service';
import { ReportService } from 'src/app/report/services/report.service';
import { Report } from 'src/app/core/models/report';
import { BaseTenantService } from 'src/app/core/services/base-tenant.service';
import { PlanningService } from 'src/app/actions/services/legacy-planning.service';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { SingleTenantOverviewComponent } from '../single-tenant-overview/single-tenant-overview.component';
import { MatCard, MatCardHeader, MatCardTitle, MatCardImage, MatCardContent, MatCardActions } from '@angular/material/card';
import { RouterLink } from '@angular/router';
import { ListItemBoxComponent } from '../../../shared/components/list-item-box/list-item-box.component';
import { MatProgressBar } from '@angular/material/progress-bar';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

interface LastWorkingAt {
  wrapped: GoalsPlanning | Report.Report;
  type: 'action' | 'report';
}

@Component({
    selector: 'app-single-tenant-stats[tenant]',
    templateUrl: './single-tenant-stats.component.html',
    styles: [`
    `
    ],
    imports: [MatIconButton, MatMenuTrigger, MatIcon, MatMenu, MatMenuItem, SingleTenantOverviewComponent, MatCard, MatCardHeader, MatCardTitle, MatCardImage, MatCardContent, MatCardActions, MatButton, RouterLink, ListItemBoxComponent, MatProgressBar, AsyncPipe, TranslateModule]
})
export class SingleTenantStatsComponent implements OnInit {
  private ps = inject(PlanningService);
  private statsSvc = inject(StatsService);
  private rs = inject(ReportService);
  private ts = inject(BaseTenantService);

  readonly tenant = input.required<Tenant.Tenant>();

  tenant$!: Observable<Tenant.Tenant>;

  allActions$!: Observable<GoalsPlanning[]>;

  selectedYear = signal(0);

  lastWorkingAt$!: Observable<LastWorkingAt | null>;

  stats$!: Observable<Dashboard.SingleTenantStats>;
  benefitsStats$!: Observable<any>;

  benefits!: { name: string; description: string }[];

  colors = ['#847cff', '#3f37ff', '#5b53ff', '#5149ff', '#665dff', '#6e65ff'];

  constructor() {
    effect(() => {
      if (this.selectedYear() === 0) {
        return;
      }

      this.stats$ = this.statsSvc.getTenantStats(this.tenant()._id, this.selectedYear());

      this.benefitsStats$ = this.stats$.pipe(
        map((stats: Dashboard.SingleTenantStats) => {
          const { benefits } = stats;
          this.benefits = benefits as any as {
            name: string;
            description: string;
          }[];
          return {
            labels: benefits.map((b) => b.name),
            datasets: [
              {
                data: benefits.map((b) => b.percent),
                label: '',
                minBarLength: 5,
                maxBarThickness: 50,
                backgroundColor: this.colors,
                hoverBackgroundColor: this.colors.map((c) => adjustHexColor(c, -30)),
              },
            ],
          };
        })
      );
    });
  }

  ngOnInit(): void {
    this.allActions$ = this.ps.getAllPlanningOf(this.tenant()._id);

    this.tenant$ = this.ts.getById(this.tenant()._id);

    // init selected year
    this.allActions$.pipe(
      take(1),
    ).subscribe(actions => {
        const max = Math.max.apply(null, actions.map(gp => gp.year));
        max > 0 ? this.selectedYear.set(max): this.selectedYear.set(0);
    });

    const allReports$ = this.rs.getAll(this.tenant()._id);

    this.lastWorkingAt$ = combineLatest([allReports$, this.allActions$])
      .pipe(
        map(([allReports, allActions]) => {
          const sortedReports = allReports.sort((a,b) => b._etag.$oid.localeCompare(a._etag.$oid));
          const sortedActions = allActions.sort((a,b) => b._etag!.$oid.localeCompare(a._etag!.$oid));

          const lastReport = sortedReports.length > 0 ? sortedReports[0] : null;
          const lastAction = sortedActions.length > 0 ? sortedActions[0] : null;

          if (lastReport) {
            if (lastAction) {
              if (lastReport._etag.$oid.localeCompare(lastAction._etag!.$oid) > 0) {
                return { wrapped: lastReport, type: 'report'} as LastWorkingAt;
              } else {
                return { wrapped: lastAction, type: 'action'} as LastWorkingAt;;
              }
            } else {
              return { wrapped: lastReport, type: 'report'} as LastWorkingAt;
            }
          } else {
            if (lastAction) {
              return { wrapped: lastAction, type: 'action'} as LastWorkingAt;
            } else {
              return null;
            }
          }
        }
      ));
  }

  getDate(date: Mongo.Date): string {
    return  moment(date.$date).format('DD/MM/YY');
  }

  findSectionIdx(sections: Dashboard.StatSection[], sectionName: string): { index: number } {
    return {
      index: sections.findIndex((section) => section.name === sectionName),
    };
  }

  reportOrActionsDate(item: Report.Report | GoalsPlanning): Date {
    return Mongo.date(item._etag!);
  }

  profileComplete(tenant: Tenant.Tenant): number {
    var ret = 0;
    if (this.isCompanyNameSet(tenant)) {
      ret+=30;
    }

    if (this.isOnePurposeSet(tenant)) {
      ret+=70;
    }

    return ret;
  }

  isCompanyNameSet(tenant: Tenant.Tenant): boolean {
    return tenant.profile && tenant.profile.companyName
      ? tenant.profile.companyName.length > 0
      : false;
  }

  isOnePurposeSet(tenant: Tenant.Tenant): boolean {
    return tenant.benefits
      ? tenant.benefits.length > 0
      : false;
  }
}
